import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  setDoc,
  query,
  orderBy,
  limit,
  startAfter,
  increment,
  DocumentSnapshot,
  Timestamp
} from '@angular/fire/firestore';

export interface MediaDocument {
  id?: string;
  name: string;
  originalUrl: string;
  webp1400Url?: string;
  webp600Url?: string;
  webp200Url?: string;
  storagePath: string;
  size: number;
  contentType: string;
  createdAt: Timestamp;
}

export interface MediaStats {
  totalFiles: number;
  totalSizeBytes: number;
  imageCount: number;
  videoCount: number;
  otherCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class MediaService {
  private readonly COLLECTION = 'media_files';
  private readonly STATS_DOC = 'media_stats/global';

  constructor(private firestore: Firestore) {}

  /**
   * Add a media document to Firestore and update global stats.
   */
  async addMediaDocument(data: Omit<MediaDocument, 'id' | 'createdAt'>): Promise<string> {
    const colRef = collection(this.firestore, this.COLLECTION);
    const docData = {
      ...data,
      createdAt: Timestamp.now()
    };

    const docRef = await addDoc(colRef, docData);

    // Update stats atomically
    const statsRef = doc(this.firestore, this.STATS_DOC);
    const typeCategory = this.getTypeCategory(data.contentType);
    const statsUpdate: Record<string, any> = {
      totalFiles: increment(1),
      totalSizeBytes: increment(data.size)
    };
    if (typeCategory === 'image') statsUpdate['imageCount'] = increment(1);
    else if (typeCategory === 'video') statsUpdate['videoCount'] = increment(1);
    else statsUpdate['otherCount'] = increment(1);

    await setDoc(statsRef, statsUpdate, { merge: true });

    return docRef.id;
  }

  /**
   * Delete a media document from Firestore and update stats.
   */
  async deleteMediaDocument(docId: string): Promise<void> {
    const docRef = doc(this.firestore, this.COLLECTION, docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;

    const data = snap.data() as MediaDocument;
    await deleteDoc(docRef);

    // Update stats atomically
    const statsRef = doc(this.firestore, this.STATS_DOC);
    const typeCategory = this.getTypeCategory(data.contentType);
    const statsUpdate: Record<string, any> = {
      totalFiles: increment(-1),
      totalSizeBytes: increment(-data.size)
    };
    if (typeCategory === 'image') statsUpdate['imageCount'] = increment(-1);
    else if (typeCategory === 'video') statsUpdate['videoCount'] = increment(-1);
    else statsUpdate['otherCount'] = increment(-1);

    await setDoc(statsRef, statsUpdate, { merge: true });
  }

  /**
   * Get a page of media files, sorted newest first.
   */
  async getMediaFiles(
    pageSize: number = 48,
    lastDoc?: DocumentSnapshot
  ): Promise<{ files: (MediaDocument & { id: string })[]; lastDoc: DocumentSnapshot | null }> {
    const colRef = collection(this.firestore, this.COLLECTION);

    let q;
    if (lastDoc) {
      q = query(colRef, orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(pageSize));
    } else {
      q = query(colRef, orderBy('createdAt', 'desc'), limit(pageSize));
    }

    const snapshot = await getDocs(q);
    const files = snapshot.docs.map(d => ({
      id: d.id,
      ...d.data()
    })) as (MediaDocument & { id: string })[];

    const last = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

    return { files, lastDoc: last };
  }

  /**
   * Get global media statistics.
   */
  async getStats(): Promise<MediaStats> {
    const statsRef = doc(this.firestore, this.STATS_DOC);
    const snap = await getDoc(statsRef);

    if (!snap.exists()) {
      return {
        totalFiles: 0,
        totalSizeBytes: 0,
        imageCount: 0,
        videoCount: 0,
        otherCount: 0
      };
    }

    const data = snap.data();
    return {
      totalFiles: data['totalFiles'] || 0,
      totalSizeBytes: data['totalSizeBytes'] || 0,
      imageCount: data['imageCount'] || 0,
      videoCount: data['videoCount'] || 0,
      otherCount: data['otherCount'] || 0
    };
  }

  /**
   * Migrate existing Storage files to Firestore index.
   * Call this once to backfill existing files.
   */
  async migrateFromStorage(
    storageFiles: { name: string; url: string; fullPath: string; size: number; contentType: string; timeCreated: string; variants?: { original?: string; webp1400?: string; webp600?: string; webp200?: string } }[],
    onProgress?: (current: number, total: number) => void
  ): Promise<number> {
    let migrated = 0;
    const total = storageFiles.length;

    // Reset stats
    const statsRef = doc(this.firestore, this.STATS_DOC);
    const stats: MediaStats = {
      totalFiles: 0,
      totalSizeBytes: 0,
      imageCount: 0,
      videoCount: 0,
      otherCount: 0
    };

    for (const file of storageFiles) {
      const colRef = collection(this.firestore, this.COLLECTION);
      const docData = {
        name: file.name,
        originalUrl: file.url,
        webp1400Url: file.variants?.webp1400 || null,
        webp600Url: file.variants?.webp600 || null,
        webp200Url: file.variants?.webp200 || null,
        storagePath: file.fullPath,
        size: file.size,
        contentType: file.contentType,
        createdAt: Timestamp.fromDate(new Date(file.timeCreated))
      };

      await addDoc(colRef, docData);

      // Accumulate stats
      stats.totalFiles++;
      stats.totalSizeBytes += file.size;
      const cat = this.getTypeCategory(file.contentType);
      if (cat === 'image') stats.imageCount++;
      else if (cat === 'video') stats.videoCount++;
      else stats.otherCount++;

      migrated++;
      onProgress?.(migrated, total);
    }

    // Write final stats
    await setDoc(statsRef, stats);

    return migrated;
  }

  private getTypeCategory(contentType: string): 'image' | 'video' | 'other' {
    if (contentType.startsWith('image/')) return 'image';
    if (contentType.startsWith('video/')) return 'video';
    return 'other';
  }
}
